import type { Coordinate, Country, GeoData } from "../game/geo.ts";
import { clues, dimsOtherContinents, focus, MAX_GUESSES, results, status, type Band } from "../game/rules.ts";
import { feedbackLine, withLine, type FeedbackLine } from "../game/feedback.ts";
import { ARROWS, clueAnnouncement, distanceShort, guessAnnouncement, statusLine } from "../game/text.ts";
import type { PageData } from "../page.ts";
import { loadGuesses, saveGuesses } from "../store.ts";
import { GuessField } from "./combobox.ts";
import { h } from "./dom.ts";
import { playSound } from "./sound.ts";
import { photoViewer } from "./viewer.ts";

export const BAND_COLOURS: Record<Band, string> = {
  correct: "#F2ECE1", nextDoor: "#E35A4B", hot: "#E8844A", warm: "#E3B24E", mild: "#B5AC86", far: "#5E7790",
};

export interface GlobeState { colours: Map<string, string>; dimmedOutside: string | null; outlined: string | null; label: string | null }
export interface GlobeLike { setState(s: GlobeState): void; turnTo(c: Coordinate, animate: boolean): void; pulse(id: string): void; destroy(): void }
export interface PlayContext {
  data: PageData;
  geo: GeoData;
  storage: Storage | undefined;
  makeGlobe: (host: HTMLElement, onPick: (id: string | null) => void) => GlobeLike;
  reducedMotion: boolean;
}

/** Saved guesses made safe to replay: known selectable ids, no repeats, nothing after the answer, at most six. */
export function restoreGuesses(saved: string[], answer: string, geo: GeoData): string[] {
  const kept: string[] = [];
  for (const id of saved) {
    if (kept.length >= MAX_GUESSES) break;
    if (!geo.country(id)?.selectable || kept.includes(id)) continue;
    kept.push(id);
    if (id === answer) break;
  }
  return kept;
}

/** This puzzle's saved guesses, cleaned by `restoreGuesses`. */
export const savedGuesses = (storage: Storage | undefined, puzzle: number, answer: string, geo: GeoData): string[] =>
  restoreGuesses(loadGuesses(storage, puzzle), answer, geo);

export function showPlay(root: HTMLElement, ctx: PlayContext, onFinished: (guesses: string[], fresh: boolean) => void): void {
  const { data, geo } = ctx;
  const answer = data.puzzle.answer;
  const guesses = savedGuesses(ctx.storage, data.puzzle.number, answer, geo);
  if (status(answer, guesses) !== "playing") { onFinished(guesses, false); return; }

  const live = h("p", { class: "visually-hidden", "aria-live": "polite" });
  const chips = h("ul", { class: "chips", "aria-label": "Your guesses" });
  // Focusable without a keyboard popping up: where focus goes after a Guess button press.
  const statusText = h("p", { class: "status soft small", tabindex: "-1" });
  const guessButton = h("button", { class: "guess", type: "button" }, "Guess");
  const globeHost = h("div", { class: "globe", "aria-hidden": "true" });

  const field = new GuessField(geo, (id) => guesses.includes(id), update, (c) => submit(c, true));
  const globe = ctx.makeGlobe(globeHost, (id) => {
    // A tap picks the country, as a suggestion would, without turning the globe.
    if (over) return;
    const c = id ? geo.country(id) : undefined;
    if (c?.selectable && !guesses.includes(c.id)) field.pick(c, true);
    else if (field.picked) field.clear();
  });
  let lastTurned: string | undefined;
  let lastPicked: string | undefined;
  /** Set once the last guess is in, while the globe holds on the answer. */
  let over = false;
  // "Closer!" over the globe after a guess; praise is larger and serif. The live region already says it.
  const banner = h("p", { class: "feedback", hidden: true });
  globeHost.append(banner);
  let bannerTimer: ReturnType<typeof setTimeout> | undefined;

  const viewer = photoViewer(data.puzzle.image.url, data.puzzle.alt);
  const lookCloser = h("button", { class: "link", type: "button" }, "↗ Look closer");
  lookCloser.addEventListener("click", () => viewer.showModal());
  guessButton.addEventListener("click", () => submit(field.selected, false));

  root.replaceChildren(h("div", { class: "page play" },
    h("header", { class: "topbar" }, h("h1", { class: "mono muted", id: "play-title", tabindex: "-1" }, "WHERE IN THE WORLD?")),
    h("img", { class: "play-photo", src: data.puzzle.image.url, alt: data.puzzle.alt }),
    lookCloser,
    globeHost,
    h("section", { class: "panel" }, chips, h("div", { class: "entry" }, field.element, guessButton), statusText, live),
    viewer));
  // The start button is gone; park focus on the title, not the input, so phones don't open the keyboard.
  document.getElementById("play-title")?.focus();

  function update(): void {
    const rs = results(answer, guesses, geo);
    chips.replaceChildren(...rs.map((r) => h("li", { class: "chip", "aria-label": guessAnnouncement(r, geo.country(r.country)!.name) },
      h("span", { class: "swatch", style: `background:${BAND_COLOURS[r.band]}` }),
      h("strong", {}, geo.country(r.country)!.name),
      r.band === "correct" ? null : h("span", { class: "mono muted" }, distanceShort(r.distanceKm)),
      r.direction === null ? null : h("span", {}, ARROWS[r.direction]!))));
    const last = rs.at(-1);
    statusText.textContent = statusLine(last, last ? geo.country(last.country)?.name : undefined, MAX_GUESSES - guesses.length);
    const selected = field.selected;
    const canGuess = selected !== undefined && !guesses.includes(selected.id);
    guessButton.disabled = !canGuess;
    const colours = new Map(rs.map((r) => [r.country, BAND_COLOURS[r.band]]));
    if (over) colours.set(answer, BAND_COLOURS.correct);  // a lost game shows where it was
    if (selected && !colours.has(selected.id)) colours.set(selected.id, "#716B60");
    globe.setState({
      colours,
      dimmedOutside: dimsOtherContinents(answer, guesses) ? geo.country(answer)!.continent : null,
      outlined: selected?.id ?? null,
      label: selected?.name ?? null,
    });
    // Typing a full name or picking a suggestion turns the globe to it; a tap doesn't (the app's rule).
    if (selected && selected.id !== lastTurned && !field.picked?.fromTap) {
      lastTurned = selected.id;
      globe.turnTo(selected.centroid, !ctx.reducedMotion);
    }
    if (!selected) lastTurned = undefined;
    // A suggestion or a globe tap gets the app's small click.
    if (field.picked && field.picked.id !== lastPicked && !over) playSound({ kind: "pick" });
    lastPicked = field.picked?.id;
  }

  /** `fromField`: Return in the field, so focus stays there; a button press mustn't open the phone keyboard. */
  function submit(country: Country | undefined, fromField: boolean): void {
    if (!country) {
      const typed = field.input.value.trim();
      if (fromField && typed) say(`No country called "${typed}".`);
      return;
    }
    if (guesses.includes(country.id)) { say(`You've already guessed ${country.name}.`); return; }
    if (!country.selectable) return;
    const cluesBefore = clues(answer, data.puzzle.hint, guesses, geo).length;
    guesses.push(country.id);
    saveGuesses(ctx.storage, data.puzzle.number, guesses);
    const rs = results(answer, guesses, geo);
    const r = rs.at(-1)!;
    const st = status(answer, guesses);
    const newClues = clues(answer, data.puzzle.hint, guesses, geo).slice(cluesBefore);
    const line = feedbackLine(rs, st, String(data.puzzle.number));
    live.textContent = withLine(line, [guessAnnouncement(r, country.name), ...newClues.map(clueAnnouncement)].join(". "));
    playSound(st === "won" ? { kind: "won" } : st === "lost" ? { kind: "lost" } : { kind: "guess", band: r.band, newClue: newClues.length > 0 });
    if (st !== "playing") over = true;
    field.clear();
    showBanner(line);
    globe.pulse(over ? answer : country.id);
    const target = focus(answer, guesses, geo);
    if (target) globe.turnTo(target, !ctx.reducedMotion);
    if (over) { finish(st === "won"); return; }
    // The app only focuses the field with a hardware keyboard; Return in the field is the web's sign of one.
    if (fromField) field.input.focus();
    else statusText.focus();
  }

  /** The latest guess's line over the globe; a wrong guess's goes after 2 seconds, praise stays until the result. */
  function showBanner(line: FeedbackLine | null): void {
    clearTimeout(bannerTimer);
    banner.classList.remove("in", "out");
    if (!line) { banner.hidden = true; return; }
    banner.textContent = line.text;
    banner.classList.toggle("praise", line.isPraise);
    banner.hidden = false;
    void banner.offsetWidth;  // restarts the entrance for a second line in a row
    banner.classList.add("in");
    if (line.isPraise) return;
    bannerTimer = setTimeout(() => {
      banner.classList.add("out");
      bannerTimer = setTimeout(() => { banner.hidden = true; }, ctx.reducedMotion ? 0 : 300);
    }, 2000);
  }

  /**
   * The game is over: the globe holds on the answer for a beat (the app's timings), then the result
   * fades in over it. A win holds even with Reduce Motion, since it's a pause, not motion.
   */
  function finish(won: boolean): void {
    update();
    statusText.focus();
    field.input.disabled = true;
    guessButton.disabled = true;
    setTimeout(() => { globe.destroy(); onFinished(guesses, true); }, won ? 1600 : ctx.reducedMotion ? 0 : 750);
  }

  /** A short note in the status line and the live region; typing again brings the usual line back. Text only, never HTML. */
  function say(text: string): void {
    statusText.textContent = text;
    // Cleared first so the same note said twice is announced twice.
    live.textContent = "";
    setTimeout(() => { live.textContent = text; }, 0);
  }

  update();
  const target = focus(answer, guesses, geo);
  if (target) globe.turnTo(target, false);
}
